/**
 * Kleine, austauschbare Teilmenge der Web Audio API.
 *
 * Die echte Implementierung nutzt AudioContext direkt. Tests nutzen eine
 * Attrappe (siehe test/testUtils/fakeAudioEnvironment.ts), damit
 * Node-Erzeugung, Verbindungen und Aufraeumen ohne echten Browser-AudioContext
 * automatisiert geprueft werden koennen. Das ersetzt keine Hoerprobe - siehe
 * LIMITATIONS.md.
 */

export interface AudioParamLike {
  value: number;
  setValueAtTime(value: number, time: number): void;
  linearRampToValueAtTime(value: number, time: number): void;
  exponentialRampToValueAtTime(value: number, time: number): void;
  cancelScheduledValues(time: number): void;
}

export interface AudioNodeLike {
  connect(destination: AudioNodeLike): AudioNodeLike;
  disconnect(): void;
}

export type OscillatorTypeLike = 'sine' | 'square' | 'sawtooth' | 'triangle';

export interface OscillatorLike extends AudioNodeLike {
  type: OscillatorTypeLike;
  frequency: AudioParamLike;
  start(time: number): void;
  stop(time: number): void;
  onended: (() => void) | null;
}

export interface GainLike extends AudioNodeLike {
  gain: AudioParamLike;
}

export interface AudioEnvironment {
  readonly currentTime: number;
  readonly destination: AudioNodeLike;
  createOscillator(): OscillatorLike;
  createGain(): GainLike;
  resume(): Promise<void>;
  close(): Promise<void>;
}

type BrowserAudioContextCtor = new () => AudioContext;

function resolveAudioContextCtor(): BrowserAudioContextCtor {
  const globalWithWebkit = globalThis as typeof globalThis & { webkitAudioContext?: BrowserAudioContextCtor };
  const ctor = globalWithWebkit.AudioContext ?? globalWithWebkit.webkitAudioContext;
  if (!ctor) {
    throw new Error('Web Audio API ist in dieser Umgebung nicht verfuegbar.');
  }
  return ctor;
}

/** Echte Web-Audio-Umgebung fuer den Browser. */
export class BrowserAudioEnvironment implements AudioEnvironment {
  private readonly context: AudioContext;

  constructor() {
    const Ctor = resolveAudioContextCtor();
    this.context = new Ctor();
  }

  get currentTime(): number {
    return this.context.currentTime;
  }

  get destination(): AudioNodeLike {
    return this.context.destination;
  }

  createOscillator(): OscillatorLike {
    return this.context.createOscillator() as unknown as OscillatorLike;
  }

  createGain(): GainLike {
    return this.context.createGain() as unknown as GainLike;
  }

  resume(): Promise<void> {
    return this.context.resume();
  }

  close(): Promise<void> {
    return this.context.close();
  }
}
